import React, { useState } from 'react';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Server, Cloud, FolderOpen, HardDrive, Globe } from 'lucide-react';
import { IntegrationConnectionUI } from './IntegrationConnectionUI';

// ── Protocol metadata ──────────────────────────────────────────────────────────
const PROTOCOLS = [
    { value: 'S3',         label: 'AWS S3',         icon: Cloud,      color: 'text-orange-500' },
    { value: 'AzureBlob',  label: 'Azure Blob Storage',     icon: Cloud,      color: 'text-blue-500'   },
    { value: 'SharePoint', label: 'Microsoft SharePoint',   icon: Globe,      color: 'text-blue-600'   },
    { value: 'OneDrive',   label: 'Microsoft OneDrive',     icon: HardDrive,  color: 'text-blue-500'   },
    { value: 'GDrive',     label: 'Google Drive',           icon: FolderOpen, color: 'text-green-500'  },
];

// ── Path placeholder per protocol ─────────────────────────────────────────────
const PATH_PLACEHOLDER: Record<string, string> = {
    S3:         'data/reports/2024.csv',
    AzureBlob:  'folder/data.csv',
    SharePoint: '/Shared Documents/Reports/data.csv',
    OneDrive:   '/Documents/data.csv',
    GDrive:     'My Drive/Reports/data.csv',
};

const PATH_HINT: Record<string, string> = {
    S3:         'Path relative to the bucket root.',
    AzureBlob:  'Path relative to the container root.',
    SharePoint: 'Path relative to the site root.',
    OneDrive:   'Path relative to your OneDrive root.',
    GDrive:     'Path relative to My Drive root.',
};

export const RemoteStorageConfig = ({ node, onChangeData }: any) => {
    const config = node.data || {};

    // ── Local protocol state so buttons respond immediately ───────────────────
    const [protocol, setProtocol] = useState<string>(config.protocol || 'S3');

    const update = (key: string, value: any) => onChangeData(key, value);

    const handleProtocol = (val: string) => {
        setProtocol(val);
        // Batch both fields in ONE call to avoid race conditions
        onChangeData({ protocol: val, connectionId: '' });
    };

    const protocolMeta = PROTOCOLS.find(p => p.value === protocol);
    const ProtocolIcon = protocolMeta?.icon ?? Server;

    return (
        <div className="space-y-5">
            {/* ── Section header ───────────────────────────────────────────── */}
            <h3 className="font-semibold text-gray-900 text-sm tracking-wide flex items-center gap-2 border-b border-gray-100 pb-2">
                <Server className="h-4 w-4 text-indigo-500" />
                Remote File Source
            </h3>

            {/* ── Protocol selector (dropdown, consistent with File Listener) ── */}
            <div className="space-y-1.5">
                <label className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Source Type</label>
                <Select value={protocol} onValueChange={handleProtocol}>
                    <SelectTrigger className="h-9 text-xs">
                        <SelectValue placeholder="Select storage type" />
                    </SelectTrigger>
                    <SelectContent className="bg-white z-[100] border border-gray-200 shadow-lg">
                        {PROTOCOLS.map(p => {
                            const Icon = p.icon;
                            return (
                                <SelectItem key={p.value} value={p.value}>
                                    <div className="flex items-center gap-2">
                                        <Icon className={`h-3.5 w-3.5 ${p.color}`} />
                                        <span>{p.label}</span>
                                    </div>
                                </SelectItem>
                            );
                        })}
                    </SelectContent>
                </Select>
            </div>

            {/* ── Integration Connection (auto-fetches from Integration Settings) ── */}
            <div>
                <div className="flex items-center gap-1.5 mb-1">
                    <ProtocolIcon className={`h-3.5 w-3.5 ${protocolMeta?.color ?? 'text-gray-500'}`} />
                    <span className="text-[11px] font-semibold text-gray-600 uppercase tracking-wide">
                        {protocolMeta?.label} Connection
                    </span>
                </div>
                <IntegrationConnectionUI
                    protocol={protocol}
                    connectionId={config.connectionId || ''}
                    onChangeConnectionId={(val) => update('connectionId', val)}
                    newConnectionLabel={config._newConnLabel}
                    newConnectionFields={config._newConnFields}
                    onChangeNewLabel={(v) => update('_newConnLabel', v)}
                    onChangeNewFields={(v) => update('_newConnFields', v)}
                />
            </div>

            {/* ── S3-specific: Bucket Name + optional endpoint ───────────────── */}
            {protocol === 'S3' && (
                <div className="space-y-3">
                    <div className="space-y-1">
                        <label className="text-[11px] font-semibold text-gray-600">Bucket Name</label>
                        <Input
                            value={config.bucketName || ''}
                            onChange={(e) => update('bucketName', e.target.value)}
                            className="h-8 text-xs"
                            placeholder="my-data-bucket"
                        />
                    </div>
                    <div className="space-y-1">
                        <label className="text-[11px] font-semibold text-gray-600">
                            Custom Endpoint <span className="text-gray-400 font-normal">(MinIO / self-hosted)</span>
                        </label>
                        <Input
                            value={config.endpoint || ''}
                            onChange={(e) => update('endpoint', e.target.value)}
                            className="h-8 text-xs font-mono"
                            placeholder="https://minio.company.com"
                        />
                        <p className="text-[10px] text-gray-400">Leave blank for standard AWS S3.</p>
                    </div>
                </div>
            )}

            {/* ── Azure Blob: Container Name ─────────────────────────────────── */}
            {protocol === 'AzureBlob' && (
                <div className="space-y-1">
                    <label className="text-[11px] font-semibold text-gray-600">Container Name</label>
                    <Input
                        value={config.bucketName || ''}
                        onChange={(e) => update('bucketName', e.target.value)}
                        className="h-8 text-xs"
                        placeholder="my-container"
                    />
                </div>
            )}

            {/* ── SharePoint: Domain + Site ──────────────────────────────────── */}
            {protocol === 'SharePoint' && (
                <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                        <label className="text-[11px] font-semibold text-gray-600">Domain</label>
                        <Input
                            value={config.sharepointDomain || ''}
                            onChange={(e) => update('sharepointDomain', e.target.value)}
                            className="h-8 text-xs"
                            placeholder="contoso"
                        />
                        <p className="text-[10px] text-gray-400">e.g. "contoso" from contoso.sharepoint.com</p>
                    </div>
                    <div className="space-y-1">
                        <label className="text-[11px] font-semibold text-gray-600">Site Name</label>
                        <Input
                            value={config.siteName || ''}
                            onChange={(e) => update('siteName', e.target.value)}
                            className="h-8 text-xs"
                            placeholder="Finance"
                        />
                    </div>
                </div>
            )}



            {/* ── OneDrive / GDrive: no extra fields (all via OAuth connection) ── */}
            {(protocol === 'OneDrive' || protocol === 'GDrive') && (
                <div className="rounded-lg bg-blue-50 border border-blue-100 px-3 py-2 text-[11px] text-blue-700">
                    Select a saved {protocol === 'GDrive' ? 'Google' : 'Microsoft'} account connection above.
                    The connection must be configured in <strong>Integrations Settings</strong> first.
                </div>
            )}

            {/* ── File Path (all protocols) ─────────────────────────────────── */}
            <div className="space-y-1">
                <label className="text-[11px] font-semibold text-gray-600">
                    File Path / Object Key
                </label>
                <Input
                    value={config.path || ''}
                    onChange={(e) => update('path', e.target.value)}
                    className="h-8 text-xs font-mono"
                    placeholder={PATH_PLACEHOLDER[protocol] || 'path/to/file.csv'}
                />
                {PATH_HINT[protocol] && (
                    <p className="text-[10px] text-gray-400">{PATH_HINT[protocol]}</p>
                )}
            </div>

            {/* ── File format hint ──────────────────────────────────────────── */}
            <div className="space-y-1">
                <label className="text-[11px] font-semibold text-gray-600">File Format</label>
                <Select
                    value={config.fileFormat || 'auto'}
                    onValueChange={(v) => update('fileFormat', v)}
                >
                    <SelectTrigger className="h-8 text-xs">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white z-[100] border border-gray-200 shadow-lg">
                        <SelectItem value="auto">Auto-detect</SelectItem>
                        <SelectItem value="csv">CSV</SelectItem>
                        <SelectItem value="xlsx">Excel (.xlsx)</SelectItem>
                        <SelectItem value="json">JSON</SelectItem>
                        <SelectItem value="parquet">Parquet</SelectItem>
                    </SelectContent>
                </Select>
            </div>
        </div>
    );
};
